import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngxs/store";
import { combineLatest, Observable, Subject, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { DashboardState } from "../store/dashboard.state";
import { PlanningFilterState } from "../store/planning-filter.state";
import {
  CheckUncheckAllReferenceLevelCandidateFiltersAction,
  CheckUncheckAllYeargroupFiltersAction,
  FilterByClassAction,
  PlanningState,
  UpdateAllCandidateCheckboxAction,
  UpdateCandidateCheckboxAction,
  UpdateReferenceLevelCandidateFiltersAction,
  UpdateYeargroupFiltersAction,
} from "../store/planning.state";
import { Candidate, CitoApplicationModule, UserGroup } from "./../model/model";
import {
  CheckboxModel,
  FilterBySearchTextAction,
  UpdateAllSubjectCheckboxAction,
} from "./../store/learning-objectives.state";

import { AsyncPipe, CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { ArraySortPipe } from "../helpers/truncate.pipe";

@Component({
  selector: "app-planning-horizontal-filters",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AsyncPipe,
    FormsModule,
    ArraySortPipe,
    BsDropdownModule,
  ],
  templateUrl: "./planning-horizontal-filters.component.html",
  styleUrls: ["./planning-horizontal-filters.component.css"],
})
export class PlanningHorizontalFiltersComponent implements OnDestroy, OnInit {
  citomodule$: Observable<CitoApplicationModule>;
  citomodules = CitoApplicationModule;
  searchText: string;
  modelChangeSubscription: Subscription;
  classFilterSubscription: Subscription;
  candidateCheckSubscription: Subscription;
  modelChanged$: Subject<string> = new Subject<string>();

  uniqueYearGroups$: Observable<any>;
  candidatesPlaceholder$: Observable<string>;
  groups$: Observable<UserGroup[]>;
  groupname$: Observable<string>;

  candidates$: Observable<Candidate[]>;
  candidatesVisible$: Observable<Candidate[]>;
  selectedClassGroupFilter: any;
  checkedCandidates: Array<any>;
  allCandidatesChecked: boolean;

  allYeargroupsChecked$: Observable<boolean>;
  yeargroupFilters$: Observable<CheckboxModel[]>;
  yeargroupPlaceholder$: Observable<string>;

  allReferenceLevelsChecked$: Observable<boolean>;
  referenceLevelsFilters$: Observable<CheckboxModel[]>;
  referenceLevelPlaceholder$: Observable<string>;

  @Input() classFilter = 0;
  @Input() yearGroupFilter = 0;
  @Input() disabled = false;
  @Input() placeholdertext: string;

  constructor(private store: Store, private router: Router) {
    this.allCandidatesChecked = true;
    this.checkedCandidates = [];

    this.candidates$ = combineLatest([
      this.store.select(PlanningState.candidates),
      this.store.select(PlanningFilterState.filter),
    ]).pipe(
      map(([candidates, f]) => {
        return candidates ? candidates?.filter(f) : candidates;
      })
    );

    this.candidatesVisible$ = this.store.select(
      PlanningState.visibleCandidates
    );

    this.groups$ = this.store.select(PlanningState.groups);
    this.groupname$ = this.store.select(PlanningState.groupname);
    this.candidatesPlaceholder$ = this.store.select(
      PlanningState.candidatesPlaceholder
    );
    this.citomodule$ = store.select(DashboardState.citomodule);

    this.referenceLevelPlaceholder$ = this.store.select(
      PlanningState.referenceLevelPlaceholder
    );
    this.yeargroupPlaceholder$ = this.store.select(
      PlanningState.yeargroupPlaceholder
    );

    this.allYeargroupsChecked$ = this.store.select(
      PlanningState.allYeargroupsChecked
    );
    this.yeargroupFilters$ = this.store.select(PlanningState.yeargroupFilters);

    this.allReferenceLevelsChecked$ = this.store.select(
      PlanningState.AllReferenceLevelsChecked
    );
    this.referenceLevelsFilters$ = this.store.select(
      PlanningState.referencelevelFilters
    );
  }

  candidatePresent(candidate: Candidate, filteredCandidates: Candidate[]) {
    return filteredCandidates.findIndex((c) => c.id === candidate.id) > -1;
  }
  yeargroupPresentInClass(yeargroup: CheckboxModel, candidates: Candidate[]) {
    return candidates.findIndex((c) => c.yearGroup == yeargroup.value) > -1;
  }

  classFilterChanged = (event, id: string) => {
    event.preventDefault();
    this.selectedClassGroupFilter = id;
    // Clean up any existing subscription before creating a new one
    if (this.classFilterSubscription) {
      this.classFilterSubscription.unsubscribe();
    }
    this.classFilterSubscription = this.store
      .dispatch(new FilterByClassAction(id))
      .subscribe(() => {
        this.store.dispatch(new UpdateAllSubjectCheckboxAction());
        this.store.dispatch(new FilterBySearchTextAction(""));
        this.router.navigate(["/"]).then(() => {
          this.router.navigate(["/plan"], {});
        });
      });
  };

  checkUncheckAllCandidates = (allCandidatesChecked: boolean) => {
    this.store.dispatch(
      new UpdateAllCandidateCheckboxAction(allCandidatesChecked)
    );
  };

  checkUncheckSingleCandidate = (candidate: any) => {
    this.store.dispatch(new UpdateCandidateCheckboxAction(candidate));
    let values: any;
    // Clean up any existing subscription before creating a new one
    if (this.candidateCheckSubscription) {
      this.candidateCheckSubscription.unsubscribe();
    }
    this.candidateCheckSubscription = this.candidates$.subscribe(
      (s) => (values = s)
    );

    const sameValues = values.every(
      (s: any) => s.checked === values[0].checked
    );

    this.allCandidatesChecked = sameValues;
  };

  checkUncheckAllYeargroup = ($event, checked: boolean) => {
    this.store.dispatch(new CheckUncheckAllYeargroupFiltersAction(checked));
  };

  checkUncheckYeargroup = ($event, checked: boolean, id: number) => {
    $event.stopPropagation();
    this.store.dispatch(
      new UpdateYeargroupFiltersAction({ id: id, value: checked })
    );
  };

  checkUncheckReferenceLevel = ($event, checked: boolean, id: number) => {
    $event.stopPropagation();
    this.store.dispatch(
      new UpdateReferenceLevelCandidateFiltersAction({ id: id, value: checked })
    );
  };

  checkUncheckAllReferenceLevel = ($event, checked: boolean) => {
    this.store.dispatch(
      new CheckUncheckAllReferenceLevelCandidateFiltersAction(checked)
    );
  };

  resetFilters = () => {
    this.allCandidatesChecked = true;
    this.store.dispatch(new CheckUncheckAllYeargroupFiltersAction(true));
    this.store.dispatch(
      new CheckUncheckAllReferenceLevelCandidateFiltersAction(true)
    );
    this.store.dispatch(new UpdateAllCandidateCheckboxAction(true));
  };

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.modelChangeSubscription) {
      this.modelChangeSubscription.unsubscribe();
    }
    if (this.classFilterSubscription) {
      this.classFilterSubscription.unsubscribe();
    }
    if (this.candidateCheckSubscription) {
      this.candidateCheckSubscription.unsubscribe();
    }
  }
}
